import { T, Var, useGT } from 'gt-react';
import {
  ArchiveIcon,
  ArrowCounterClockwiseIcon,
  CircleNotchIcon,
  EyeIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
  type Icon,
} from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import type {
  TaskTypeActivityEntry,
  TaskTypeActivityKind,
  TaskTypeActivityUserGroupRef,
} from '@erp/rpc';
import { useCan } from 'src/hooks/useCan';
import { Button } from 'src/components/ui/button';
import {
  ActivityRail,
  ActivityWhen,
  type ActivityTone,
} from 'src/components/common/activity-timeline/ActivityTimelinePrimitives';
import { ActivityActorLabel } from 'src/components/common/activity-timeline/ActivityActorLabel';
import { ActivityFieldDiffs } from 'src/components/common/activity-timeline/ActivityFieldDiffs';
import { useTaskTypeActivity } from 'src/api/rpc/hooks/tasks/useTaskTypes';
import { TaskTypeSection } from './TaskTypeEditorSections';

/**
 * A task type's Activity timeline: every recorded change to its configuration
 * (creation, setting edits with field diffs, viewer group changes, archive and
 * restore), newest first. Read-only; read off the audit log.
 */
export function TaskTypeActivitySection({
  taskTypeId,
}: {
  taskTypeId: string;
}) {
  const [canRead, { isLoading: isCanLoading }] = useCan(
    Action.Read,
    Subject.TaskType,
  );
  const activity = useTaskTypeActivity(taskTypeId, canRead);

  return (
    <TaskTypeSection
      title={<T context="task type editor section title">Activity</T>}
      description={
        <T context="task type editor activity section description">
          Who changed this task type's settings, and when.
        </T>
      }
    >
      {isCanLoading || (canRead && activity.isLoading) ? (
        <div className="flex items-center justify-center py-4 text-muted-foreground">
          <CircleNotchIcon className="h-4 w-4 animate-spin" />
        </div>
      ) : !canRead ? (
        <p className="text-sm text-muted-foreground">
          <T context="task type activity section, viewer lacks permission">
            You don't have permission to see this task type's activity.
          </T>
        </p>
      ) : activity.isError ? (
        <p className="text-sm text-destructive">
          <T context="task type activity failed to load">
            Couldn't load this task type's activity.
          </T>
        </p>
      ) : activity.entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <T context="empty state of the task type activity section">
            No activity recorded yet.
          </T>
        </p>
      ) : (
        <div>
          <ul className="px-1">
            {activity.entries.map((entry, i) => (
              <ActivityEntryRow
                key={entry.id}
                entry={entry}
                isLast={i === activity.entries.length - 1}
              />
            ))}
          </ul>
          <div className="flex items-center justify-between px-1 pt-2 text-xs text-muted-foreground">
            <span>
              <T context="footer of the task type activity section, N of M entries shown">
                Showing <Var>{activity.entries.length}</Var> of{' '}
                <Var>{activity.total}</Var>
              </T>
            </span>
            {activity.hasNextPage && (
              <Button
                variant="outline"
                size="sm"
                disabled={activity.isFetchingNextPage}
                onClick={() => void activity.fetchNextPage()}
              >
                {activity.isFetchingNextPage && (
                  <CircleNotchIcon className="h-3.5 w-3.5 animate-spin" />
                )}
                <T context="button, load older task type activity entries">
                  Load more
                </T>
              </Button>
            )}
          </div>
        </div>
      )}
    </TaskTypeSection>
  );
}

const KIND_PRESENTATION: Record<
  TaskTypeActivityKind,
  { tone: ActivityTone; Icon: Icon }
> = {
  created: { tone: 'green', Icon: PlusIcon },
  updated: { tone: 'stone', Icon: PencilSimpleIcon },
  viewers_changed: { tone: 'purple', Icon: EyeIcon },
  archived: { tone: 'amber', Icon: ArchiveIcon },
  restored: { tone: 'green', Icon: ArrowCounterClockwiseIcon },
  deleted: { tone: 'amber', Icon: TrashIcon },
};

function KindTitle({ kind }: { kind: TaskTypeActivityKind }) {
  switch (kind) {
    case 'created':
      return <T context="task type activity entry">Task type created</T>;
    case 'updated':
      return <T context="task type activity entry">Settings changed</T>;
    case 'viewers_changed':
      return <T context="task type activity entry">Queue viewers changed</T>;
    case 'archived':
      return <T context="task type activity entry">Task type archived</T>;
    case 'restored':
      return <T context="task type activity entry">Task type restored</T>;
    case 'deleted':
      return <T context="task type activity entry">Task type deleted</T>;
  }
}

function ActivityEntryRow({
  entry,
  isLast,
}: {
  entry: TaskTypeActivityEntry;
  isLast: boolean;
}) {
  const { tone, Icon } = KIND_PRESENTATION[entry.kind];
  const labelFor = useFieldLabel();
  return (
    <ActivityRail
      tone={tone}
      icon={<Icon className="h-3.5 w-3.5" />}
      isLast={isLast}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-sm font-medium text-foreground">
          <KindTitle kind={entry.kind} />
        </span>
        <ActivityActorLabel actor={entry.actor} />
        <ActivityWhen at={entry.timestamp} />
      </div>
      {entry.viewerGroups ? (
        <ViewerGroupChanges
          added={entry.viewerGroups.added}
          removed={entry.viewerGroups.removed}
        />
      ) : null}
      <ActivityFieldDiffs
        changedFields={entry.changedFields}
        labelFor={labelFor}
        expandStructured
      />
    </ActivityRail>
  );
}

function GroupNames({ groups }: { groups: TaskTypeActivityUserGroupRef[] }) {
  const gt = useGT();
  const deleted = gt('a deleted group', {
    context: 'placeholder for a user group that no longer exists',
  });
  return <>{groups.map((group) => group.name ?? deleted).join(', ')}</>;
}

function ViewerGroupChanges({
  added,
  removed,
}: {
  added: TaskTypeActivityUserGroupRef[];
  removed: TaskTypeActivityUserGroupRef[];
}) {
  return (
    <div className="mt-0.5 flex flex-col text-xs text-muted-foreground">
      {added.length > 0 && (
        <span>
          <T context="task type activity detail, user groups given queue access">
            Added{' '}
            <Var>
              <GroupNames groups={added} />
            </Var>
          </T>
        </span>
      )}
      {removed.length > 0 && (
        <span>
          <T context="task type activity detail, user groups whose queue access was removed">
            Removed{' '}
            <Var>
              <GroupNames groups={removed} />
            </Var>
          </T>
        </span>
      )}
    </div>
  );
}

/** Human labels for `core_task_types` columns; unknown columns show raw. */
function useFieldLabel(): (field: string) => string {
  const gt = useGT();
  const context = 'task type activity, name of a changed setting';
  const labels: Record<string, string> = {
    name: gt('Name', { context }),
    description: gt('Description', { context }),
    anchor_entity_type: gt('Anchor record type', { context }),
    expected_subtask_type_keys: gt('Checklist', { context }),
    completion_mode: gt('Completion mode', { context }),
    outcome_enum_id: gt('Outcome options', { context }),
    outcome_fields: gt('Outcome fields', { context }),
    actions: gt('Actions', { context }),
    procedures: gt('Playbook', { context }),
    task_namespace_id: gt('Namespace', { context }),
    default_assignee_type: gt('Default assignee type', { context }),
    default_assignee_user_id: gt('Default assignee user', { context }),
    default_assignee_agent_definition_id: gt('Default assignee agent', {
      context,
    }),
    default_assignee_user_group_id: gt('Default assignee group', { context }),
    default_assignee_allocation: gt('Default assignee allocation', {
      context,
    }),
    escalation_target_type: gt('Escalation target type', { context }),
    escalation_target_user_id: gt('Escalation target user', { context }),
    escalation_target_user_group_id: gt('Escalation target group', {
      context,
    }),
    escalation_target_allocation: gt('Escalation target allocation', {
      context,
    }),
    input_schema_definition_id: gt('Input schema', { context }),
    view: gt('Review page layout', { context }),
    metrics: gt('Metric cards', { context }),
  };
  return (field) => labels[field] ?? field;
}
